/**
 * Mystery Box Component
 * VOY-133: Post-game random reward system
 */

import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Gift, Sparkles, Coins } from 'lucide-react';

interface MysteryReward {
  type: 'coins' | 'booster' | 'entry' | 'upgrade' | 'jackpot';
  amount: number;
  description: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

interface MysteryBoxProps {
  onSpin: () => MysteryReward;
  onClaim: (reward: MysteryReward) => void;
}

export function MysteryBox({ onSpin, onClaim }: MysteryBoxProps) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [reward, setReward] = useState<MysteryReward | null>(null);

  const handleSpin = () => {
    setIsSpinning(true);
    setReward(null);

    // Simulate spin animation
    setTimeout(() => {
      const rewardData = onSpin();
      setReward(rewardData);
      setIsSpinning(false);
    }, 2000);
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'from-gray-400 to-gray-600';
      case 'rare': return 'from-blue-400 to-blue-600';
      case 'epic': return 'from-purple-400 to-purple-600';
      case 'legendary': return 'from-yellow-400 to-orange-500';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  return (
    <Card className="rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border-2 border-purple-300 dark:border-purple-500/50 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
      <CardContent className="p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sparkles className="w-6 h-6 text-purple-600" />
            <h3 className="text-2xl font-bold text-gray-900">Bonus Spin Available!</h3>
          </div>
          <p className="text-sm text-gray-600">
            You played today - claim your spin
          </p>
        </div>

        {/* Gift Box */}
        <div className="flex justify-center mb-6">
          <div className={`w-32 h-32 ${isSpinning ? 'animate-bounce' : ''}`}>
            <Gift 
              className={`w-full h-full ${
                isSpinning ? 'text-yellow-500 animate-spin' : 'text-purple-500'
              }`} 
            />
          </div>
        </div>

        {/* Reward Display */}
        {reward && !isSpinning && (
          <div className={`bg-gradient-to-r ${getRarityColor(reward.rarity)} text-white rounded-xl p-4 mb-4 animate-pulse`}>
            <p className="text-center text-sm uppercase font-bold mb-1 tracking-wide">
              {reward.rarity} Reward!
            </p>
            <div className="text-center">
              <p className="text-3xl font-bold mb-1">
                {reward.type === 'coins' && '🪙'}
                {reward.type === 'booster' && '🚀'}
                {reward.type === 'entry' && '🎟️'}
                {reward.type === 'upgrade' && '⭐'}
                {reward.type === 'jackpot' && '💎'}
              </p>
              <p className="text-lg font-semibold">{reward.description}</p>
            </div>
          </div>
        )}

        {/* Possible Rewards List */}
        {!reward && !isSpinning && (
          <div className="bg-white/50 rounded-lg p-3 mb-4">
            <p className="text-xs font-semibold text-gray-700 mb-2">Possible Rewards:</p>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• 50-500 Voy Coins (60%)</li>
              <li>• Free game entry (25%)</li>
              <li>• 2x boosters (10%)</li>
              <li>• Pool upgrade (5%)</li>
              <li className="text-yellow-600 font-bold">• Jackpot: $100 travel credit (1%)</li>
            </ul>
          </div>
        )}

        {/* Action Button */}
        {!reward && (
          <Button
            onClick={handleSpin}
            disabled={isSpinning}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold text-lg py-6 rounded-full"
          >
            {isSpinning ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                Spinning...
              </>
            ) : (
              <>
                <Gift className="w-5 h-5 mr-2" />
                SPIN NOW
              </>
            )}
          </Button>
        )}

        {/* Claim Button */}
        {reward && !isSpinning && (
          <Button
            onClick={() => onClaim(reward)}
            className="w-full bg-green-500 hover:bg-green-600 text-white font-bold text-lg py-6 rounded-full"
          >
            <Coins className="w-5 h-5 mr-2" />
            Claim Reward
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

